import type { BadgeTone } from '@fas/ui';
import type { EventAttendanceStatus, EventAudienceScope, EventCheckInMode, InstitutionalEventStatus, InstitutionalEventType, PermissionCode } from '@fas/types';
import { Permissions as P } from '@fas/types';

type Label = { label: string; tone: BadgeTone };

export const EVENT_TYPE: Record<InstitutionalEventType, string> = {
  MEETING: 'Meeting',
  CEREMONY: 'Ceremony',
  SEMINAR: 'Seminar',
  TRAINING: 'Training',
  ASSEMBLY: 'Assembly',
  OTHER: 'Other event',
};

export const EVENT_STATUS: Record<InstitutionalEventStatus, Label> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  PUBLISHED: { label: 'Published', tone: 'info' },
  CANCELLED: { label: 'Cancelled', tone: 'danger' },
};

export const EVENT_ATTENDANCE: Record<EventAttendanceStatus, Label> = {
  EXPECTED: { label: 'Not checked in', tone: 'neutral' },
  PRESENT: { label: 'Present', tone: 'success' },
  LATE: { label: 'Late', tone: 'warning' },
  EXCUSED: { label: 'Excused', tone: 'info' },
  ABSENT: { label: 'Absent', tone: 'danger' },
};

export const AUDIENCE_SCOPE: Record<EventAudienceScope, string> = {
  INSTITUTION: 'All faculty',
  CAMPUS: 'A campus',
  COLLEGE: 'A college',
  DEPARTMENT: 'A department',
  EMPLOYMENT_TYPE: 'An employment type',
  FACULTY: 'A faculty member',
};

export const CHECK_IN_MODE: Record<EventCheckInMode, string> = {
  QR_OR_APP: 'Code at the venue, or in the app',
  QR_ONLY: 'Code at the venue only',
};

export const EVENT_MODALITY: Record<'FACE_TO_FACE' | 'ONLINE_SYNCHRONOUS' | 'HYBRID', string> = {
  FACE_TO_FACE: 'On site',
  ONLINE_SYNCHRONOUS: 'Online',
  HYBRID: 'On site and online',
};

export const EVENT_READERS: PermissionCode[] = [P.EVENT_READ, P.EVENT_MANAGE];
export const EVENT_AREA: PermissionCode[] = [P.EVENT_CHECK_IN, P.EVENT_READ, P.EVENT_MANAGE];

/** "Nov 5, 2026 · 9:00 AM – 11:00 AM". */
export function eventWhen(e: { eventDate: string; startsAt: string; endsAt: string }, timeZone: string): string {
  const time = (iso: string) => new Intl.DateTimeFormat('en-PH', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  const [y, m, d] = e.eventDate.split('-').map(Number) as [number, number, number];
  const date = new Intl.DateTimeFormat('en-PH', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(Date.UTC(y, m - 1, d)));
  return `${date} · ${time(e.startsAt)} – ${time(e.endsAt)}`;
}

export function eventPlace(e: { modality: string; locationName: string | null; venue: string | null }): string {
  if (e.modality === 'ONLINE_SYNCHRONOUS') return 'Online';
  const place = e.locationName ?? e.venue ?? 'Venue to be announced';
  return e.modality === 'HYBRID' ? `${place} and online` : place;
}
